import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaClipboardCheck } from 'react-icons/fa6';

import { Container, Row, Col } from 'react-bootstrap';
import { Input, Fieldset, BackLink, Card, Details, Button, ErrorSummary, Select, Checkboxes, Tag } from 'nhsuk-react-components';
import { listImplants, createImplant, updateImplant, deleteImplant, getImplantDetails } from '../../actions/implantActions';
import { IMPLANT_CREATE_RESET, IMPLANT_UPDATE_RESET } from '../../constants/implantConstants';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Callout from '../../components/callout/Callout.component';
import Modal from '../../components/modal/Modal.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

const emptyImplant = {
    modelName: '',
    barcode: '',
    manufacturer: '',
    modelNumber: '',
    modelCategory: '',
    mrConditional: false,
};

export default function ImplantCRUDScreen({ history }) {
    const dispatch = useDispatch();

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;
    const isSuperUser = userInfo?.roles?.includes('SuperUser');

    useEffect(() => {
        document.title = "Edit Implants";
    }, []);

    useEffect(() => {
        dispatch({ type: IMPLANT_CREATE_RESET });
        dispatch({ type: IMPLANT_UPDATE_RESET });
    }, [dispatch]);

    const [implantListFilter, setimplantListFilter] = useState({ query: '', page: 1, filter: 'barcode' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newImplant, setNewImplant] = useState({ ...emptyImplant });
    const [updatedImplant, setUpdatedImplant] = useState({});

    const implantList = useSelector((state) => state.implantList);
    const { loading, error, implants, currentPage, totalPages, totalCount } = implantList;

    const implantCreate = useSelector((state) => state.implantCreate);
    const { success: successCreate, error: implantCreateErrors, loading: implantCreateLoading } = implantCreate;

    const implantUpdate = useSelector((state) => state.implantUpdate);
    const { success: successUpdate, loading: implantUpdateProcessing } = implantUpdate;

    const implantDetails = useSelector((state) => state.implantDetails);
    const { implant: implantDet, loading: implantDetailsLoading } = implantDetails;

    useEffect(() => {
        dispatch(listImplants(implantListFilter));
    }, [dispatch, implantListFilter]);

    useEffect(() => {
        if (implantDet) {
            setUpdatedImplant({ ...implantDet });
        }
    }, [implantDet]);

    useEffect(() => {
        if (successCreate) {
            setShowCreateModal(false);
            setNewImplant({ ...emptyImplant });
            dispatch(listImplants(implantListFilter));
            dispatch({ type: IMPLANT_CREATE_RESET });
        }
    }, [successCreate, dispatch, implantListFilter]);

    const handlePageChange = (newPage) => {
        setimplantListFilter({ ...implantListFilter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setimplantListFilter({ ...implantListFilter, query: e.target.value, page: 1 });
    };

    const handleFilterChange = (e) => {
        setimplantListFilter({ ...implantListFilter, filter: e.target.value, page: 1 });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this implant?')) {
            dispatch(deleteImplant(id)).then(() => {
                dispatch(listImplants(implantListFilter));
                if (updatedImplant?.id === id) {
                    setShowEditModal(false);
                    setUpdatedImplant({});
                }
            });
        }
    };

    const handleEditImplant = (id) => {
        dispatch(getImplantDetails(id));
        setShowCreateModal(false);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setUpdatedImplant({});
        dispatch({ type: IMPLANT_UPDATE_RESET });
    };

    const handleCreateImplant = () => {
        dispatch(createImplant(newImplant));
    };

    const handleUpdateImplant = () => {
        dispatch(updateImplant(updatedImplant.id, updatedImplant)).then(() => {
            dispatch(getImplantDetails(updatedImplant.id));
            dispatch(listImplants(implantListFilter));
        });
    };

    const openCreateModal = () => {
        setNewImplant({ ...emptyImplant });
        dispatch({ type: IMPLANT_CREATE_RESET });
        setShowEditModal(false);
        setShowCreateModal(true);
    };

    const modelCategoryOptions = (
        <>
            <Select.Option value="">Select a model category</Select.Option>
            <Select.Option value="Accessory">Accessory</Select.Option>
            <Select.Option value="Generator">Generator</Select.Option>
            <Select.Option value="Lead">Lead</Select.Option>
            <Select.Option value="Implantable Monitor">Implantable Monitor</Select.Option>
        </>
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md="12">
                            <div className="welcome-box">
                                <PageWrapper title="Implant Registry" totalCount={totalCount} loading={loading} />
                                <BackLink
                                    asElement="a"
                                    href="#"
                                    onClick={() => history.goBack()}
                                >
                                    Return back
                                </BackLink>
                                <div className="compact-admin-table__actions nhsuk-u-margin-bottom-4">
                                    <Button
                                        className="nhsuk-button--small"
                                        onClick={openCreateModal}
                                    >
                                        + Create Implant
                                    </Button>
                                    {isSuperUser && (
                                        <Button
                                            className="nhsuk-button--small"
                                            onClick={() => history.push('/pending_implants/review')}
                                        >
                                            <FaClipboardCheck style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                            Review Pending Implants
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <div className="nhsuk-grid-column-one-half">
                        <Card>
                            <Card.Content>
                                <Card.Heading className="nhsuk-heading-m">
                                    <Input
                                        id="search-implants"
                                        label="Search implants"
                                        name="search-implants"
                                        type="search"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchChange(e);
                                            }
                                        }}
                                    />
                                    <Select
                                        id="filter-implants"
                                        label="Filter by"
                                        name="filter-implants"
                                        onChange={handleFilterChange}
                                    >
                                        <Select.Option value="Barcode">Barcode/GTIN</Select.Option>
                                        <Select.Option value="ModelName">Model Name</Select.Option>
                                        <Select.Option value="ModelNumber">Model Number</Select.Option>
                                        <Select.Option value="Manufacturer">Manufacturer</Select.Option>
                                    </Select>
                                </Card.Heading>
                                <Card.Description>
                                    <Fieldset>
                                        {implants && implants.length > 0 ? (
                                            implants.map((implant) => (
                                                <Details key={implant.id || implant._id}>
                                                    <Details.Summary>
                                                        {implant.modelName}
                                                    </Details.Summary>
                                                    <Details.Text>
                                                        <p>
                                                            <strong>Manufacturer:</strong> {implant.manufacturer}
                                                        </p>
                                                        <p>
                                                            <strong>Barcode/GTIN:</strong> {implant.barcode}
                                                        </p>
                                                        <p>
                                                            <strong>Model Number:</strong> {implant.modelNumber}
                                                        </p>
                                                        <p>
                                                            <strong>MRI Conditional:</strong>{' '}
                                                            <Tag color={implant.mrConditional ? 'green' : 'red'}>
                                                                {implant.mrConditional ? 'Yes' : 'No'}
                                                            </Tag>
                                                        </p>
                                                        <div className="compact-admin-table__actions">
                                                            <Button
                                                                className="nhsuk-button--small"
                                                                disabled={implantDetailsLoading}
                                                                onClick={() => handleEditImplant(implant.id)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                secondary
                                                                className="nhsuk-button--small btn-delete"
                                                                onClick={() => handleDelete(implant.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Details.Text>
                                                </Details>
                                            ))
                                        ) : null}
                                    </Fieldset>
                                    {loading ? (
                                        <AppSpinner />
                                    ) : error ? (
                                        <p>{error}</p>
                                    ) : (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </Card.Description>
                            </Card.Content>
                        </Card>
                    </div>

                    <div className="nhsuk-grid-column-one-half" />
                </Container>
            </div>

            <Modal
                show={showCreateModal}
                handleClose={() => {
                    setShowCreateModal(false);
                    dispatch({ type: IMPLANT_CREATE_RESET });
                }}
                theme="light"
                maxWidth="720px"
            >
                <h2 className="nhsuk-heading-l">Create Implant</h2>
                {implantCreateErrors && (
                    <ErrorSummary aria-labelledby="error-summary-title" role="alert" tabIndex="-1">
                        <ErrorSummary.Title id="error-summary-title">There is a problem</ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {implantCreateErrors.map((err) => (
                                    <li key={err}>{err}</li>
                                ))}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}
                <Input
                    id="modelName-create"
                    label="Model Name"
                    name="modelName-create"
                    type="text"
                    onChange={(e) => setNewImplant({ ...newImplant, modelName: e.target.value })}
                    value={newImplant?.modelName || ''}
                    required
                />
                <Input
                    id="barcode-create"
                    label="Barcode/GTIN"
                    name="barcode-create"
                    type="text"
                    onChange={(e) => setNewImplant({ ...newImplant, barcode: e.target.value })}
                    value={newImplant?.barcode || ''}
                    required
                />
                <Input
                    id="manufacturer-create"
                    label="Manufacturer"
                    name="manufacturer-create"
                    type="text"
                    onChange={(e) => setNewImplant({ ...newImplant, manufacturer: e.target.value })}
                    value={newImplant?.manufacturer || ''}
                    required
                />
                <Input
                    id="modelNumber-create"
                    label="Model Number"
                    name="modelNumber-create"
                    type="text"
                    onChange={(e) => setNewImplant({ ...newImplant, modelNumber: e.target.value })}
                    value={newImplant?.modelNumber || ''}
                    required
                />
                <Select
                    id="modelCategory-create"
                    label="Model Category"
                    name="modelCategory-create"
                    onChange={(e) => setNewImplant({ ...newImplant, modelCategory: e.target.value })}
                    value={newImplant?.modelCategory || ''}
                    required
                >
                    {modelCategoryOptions}
                </Select>
                <Checkboxes
                    name="mrConditional-create"
                    hint="Select if the implant is MRI Conditional"
                >
                    <Checkboxes.Box
                        conditional={<p>MRI Conditional</p>}
                        onChange={(e) => setNewImplant({ ...newImplant, mrConditional: e.target.checked })}
                        checked={newImplant?.mrConditional || false}
                        id="mrConditional-create"
                        value="mrConditional"
                        label="MRI Conditional"
                    >
                        The implant is MRI Conditional
                    </Checkboxes.Box>
                </Checkboxes>
                <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                    <Button
                        className="nhsuk-button--small"
                        disabled={implantCreateLoading || implantUpdateProcessing}
                        onClick={handleCreateImplant}
                    >
                        Create Implant
                    </Button>
                    <Button
                        secondary
                        className="nhsuk-button--small"
                        onClick={() => {
                            setShowCreateModal(false);
                            dispatch({ type: IMPLANT_CREATE_RESET });
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>

            <Modal
                show={showEditModal}
                handleClose={handleCloseEdit}
                theme="light"
                maxWidth="720px"
            >
                <h2 className="nhsuk-heading-l">Edit Implant</h2>
                {implantDetailsLoading ? (
                    <AppSpinner />
                ) : (
                    <>
                        <Input
                            id="modelName"
                            label="Model Name"
                            name="modelName"
                            type="text"
                            onChange={(e) => setUpdatedImplant({ ...updatedImplant, modelName: e.target.value })}
                            value={updatedImplant.modelName ? updatedImplant.modelName : ''}
                            required
                        />
                        <Input
                            id="barcode"
                            label="Barcode/GTIN"
                            name="barcode"
                            type="text"
                            onChange={(e) => setUpdatedImplant({ ...updatedImplant, barcode: e.target.value })}
                            value={updatedImplant.barcode ? updatedImplant.barcode : ''}
                            required
                        />
                        <Input
                            id="manufacturer"
                            label="Manufacturer"
                            name="manufacturer"
                            type="text"
                            onChange={(e) => setUpdatedImplant({ ...updatedImplant, manufacturer: e.target.value })}
                            value={updatedImplant.manufacturer ? updatedImplant.manufacturer : ''}
                            required
                        />
                        <Input
                            id="modelNumber"
                            label="Model Number"
                            name="modelNumber"
                            type="text"
                            onChange={(e) => setUpdatedImplant({ ...updatedImplant, modelNumber: e.target.value })}
                            value={updatedImplant.modelNumber ? updatedImplant.modelNumber : ''}
                            required
                        />
                        <Select
                            id="modelCategory"
                            label="Model Category"
                            name="modelCategory"
                            onChange={(e) => setUpdatedImplant({ ...updatedImplant, modelCategory: e.target.value })}
                            value={updatedImplant.modelCategory ? updatedImplant.modelCategory : ''}
                            required
                        >
                            {modelCategoryOptions}
                        </Select>
                        <Checkboxes
                            name="mrConditional"
                            hint="Select if the implant is MRI Conditional"
                        >
                            <Checkboxes.Box
                                conditional={<p>The implant is MRI Conditional</p>}
                                onChange={(e) => setUpdatedImplant({ ...updatedImplant, mrConditional: e.target.checked })}
                                checked={updatedImplant.mrConditional ? updatedImplant.mrConditional : false}
                                id="mrConditional"
                                value="mrConditional"
                                label="MRI Conditional"
                            >
                                MRI Conditional?
                            </Checkboxes.Box>
                        </Checkboxes>
                        {successUpdate && (
                            <Callout
                                label="Success"
                                header="Success!"
                                autoDismiss={true}
                                dismissTime={5000}
                                time={new Date().toLocaleTimeString()}
                                title="Success!"
                                message="The implant was updated successfully."
                                mode="success"
                            />
                        )}
                        <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                            <Button
                                className="nhsuk-button--small"
                                disabled={implantUpdateProcessing}
                                onClick={handleUpdateImplant}
                            >
                                Save changes
                            </Button>
                            <Button
                                secondary
                                className="nhsuk-button--small"
                                onClick={handleCloseEdit}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
}
